import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Camera, X, Loader2, Check, AlertTriangle, PackageX, PackageOpen, Save, Undo2, Repeat, Send, Info, ArrowRight } from "lucide-react";
import DiscrepancyReportDialog from "@/components/DiscrepancyReportDialog";
import ProductPicker from "@/components/ProductPicker";
import { useAuth } from "@/context/AuthContext";

const STATES = [
  { key: "ok", label: "Tam", color: "bg-emerald-100 text-emerald-700 border-emerald-200", activeColor: "bg-emerald-600 text-white border-emerald-600", Icon: Check },
  { key: "missing", label: "Eksik", color: "bg-amber-100 text-amber-700 border-amber-200", activeColor: "bg-amber-600 text-white border-amber-600", Icon: PackageX },
  { key: "wrong", label: "Yanlış", color: "bg-rose-100 text-rose-700 border-rose-200", activeColor: "bg-rose-600 text-white border-rose-600", Icon: AlertTriangle },
  { key: "damaged", label: "Hasarlı", color: "bg-orange-100 text-orange-700 border-orange-200", activeColor: "bg-orange-600 text-white border-orange-600", Icon: PackageOpen },
  { key: "returned", label: "İade", color: "bg-violet-100 text-violet-700 border-violet-200", activeColor: "bg-violet-600 text-white border-violet-600", Icon: Undo2 },
  { key: "exchange", label: "Değişim", color: "bg-teal-100 text-teal-700 border-teal-200", activeColor: "bg-teal-600 text-white border-teal-600", Icon: Repeat },
];

export default function ReceiptCheckPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [lines, setLines] = useState([]);
  const [overall, setOverall] = useState("");
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  // Warehouse/admin work from the Depo Kutusu; everyone else from their own request.
  const canSeeBox = user?.role === "admin" || user?.role === "warehouse";
  const backTo = canSeeBox ? "/depo-kutusu" : `/talepler/${id}`;
  const backLabel = canSeeBox ? "Depo Kutusu" : "Talep Detayı";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/requests/${id}`);
        setReq(data);
        const items = (data.items && data.items.length > 0) ? data.items : [{
          product_code: data.product_code, product_name: data.product_name,
          quantity: data.quantity, unit: data.unit,
        }];
        // Prefill from previous receipt if exists
        const prev = data.receipt?.items || [];
        const byCode = Object.fromEntries(prev.map((p) => [p.product_code, p]));
        // A previous exchange swaps the request item to the chosen replacement
        // product code. Map that new code back to its origin receipt line so we
        // can tell the checker "this item is the replacement requested for X".
        const byExchangeNew = Object.fromEntries(
          prev.filter((p) => p.state === "exchange" && p.exchange_to?.product_code)
              .map((p) => [p.exchange_to.product_code, p])
        );
        setLines(items.map((it) => {
          const pc = byCode[it.product_code];
          const origin = byExchangeNew[it.product_code];
          return {
            product_code: it.product_code,
            product_name: it.product_name,
            unit: it.unit || "",
            ordered_qty: it.quantity,
            received_qty: pc?.received_qty ?? it.quantity,
            state: pc?.state || "ok",
            note: pc?.note || "",
            photo_paths: pc?.photo_paths || [],
            done: pc?.done || false,
            resolved: pc?.resolved || false,
            resolution_note: pc?.resolution_note || "",
            exchange_to_code: pc?.exchange_to?.product_code || "",
            exchange_to_name: pc?.exchange_to?.product_name || "",
            exchange_to_unit: pc?.exchange_to?.unit || "",
            // Context from the previous check (display-only hints).
            prev_state: pc?.state || (origin ? "exchange_target" : null),
            replaces_code: origin?.product_code || "",
            replaces_name: origin?.product_name || "",
          };
        }));
        setOverall(data.receipt?.overall_note || "");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
        navigate(-1);
      }
    })();
  }, [id, navigate]);

  const setLine = (code, patch) => setLines((p) => p.map((l) => {
    if (l.product_code !== code) return l;
    const next = { ...l, ...patch };
    // On any state change the line starts UN-resolved: İade/Değişim and other
    // problem states stay open until manually marked "Çözüldü" (iade işlendi /
    // değişim ürünü geldi), so the status can reflect "İade Bekleniyor" /
    // "Değişim Bekleniyor" until then.
    if (patch.state !== undefined) {
      next.resolved = false;
      next.resolution_note = "";
      // Leaving the exchange state clears the chosen replacement product.
      if (patch.state !== "exchange") {
        next.exchange_to_code = "";
        next.exchange_to_name = "";
        next.exchange_to_unit = "";
      }
    }
    return next;
  }));

  const buildReceiptPayload = () => ({
    items: lines.map((l) => ({
      product_code: l.product_code,
      received_qty: Number(l.received_qty) || 0,
      state: l.state,
      note: l.note,
      photo_paths: l.photo_paths,
      resolved: !!l.resolved,
      resolution_note: l.resolution_note || "",
      exchange_to_code: l.state === "exchange" ? (l.exchange_to_code || "") : "",
    })),
    overall_note: overall,
  });

  const handleSubmit = async () => {
    if (lines.some((l) => l.state === "exchange" && !l.exchange_to_code)) {
      toast.error("Değişim seçilen kalem(ler) için yeni ürün seçmelisiniz");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/requests/${id}/receipt`, buildReceiptPayload());
      if (data.status === "Tam Geldi") {
        toast.success("Kontrol tamamlandı — tüm kalemler tam geldi");
        navigate(`/talepler/${id}`);
      } else if (data.status === "Kapatıldı") {
        toast.success("Sipariş kapatıldı — sorunlu kalemler çözüldü olarak işaretlendi");
        navigate(`/talepler/${id}`);
      } else {
        toast.success("Kaydedildi — eksik/hatalı kalemler kontrol için açık bırakıldı");
        navigate(backTo);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  // Persist the current receipt (so photos/states/notes are stored) then open the
  // "Durumu İlet" dialog without navigating away.
  const openReport = async () => {
    if (lines.some((l) => l.state === "exchange" && !l.exchange_to_code)) {
      toast.error("Değişim seçilen kalem(ler) için yeni ürün seçmelisiniz");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/requests/${id}/receipt`, buildReceiptPayload());
      setReportOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  if (!req) return <div className="text-slate-400">Yükleniyor...</div>;

  const isLinePerfect = (l) => l.state === "ok" && Number(l.received_qty) >= Number(l.ordered_qty);
  const isLineClosed = (l) => isLinePerfect(l) || !!l.resolved;
  const closedCount = lines.filter(isLineClosed).length;
  const allPerfect = lines.length > 0 && lines.every(isLinePerfect);
  const allClosed = lines.length > 0 && lines.every(isLineClosed);
  const anyResolved = lines.some((l) => l.resolved && !isLinePerfect(l));
  const willClose = allClosed && anyResolved; // closing with resolutions → "Kapatıldı"
  const problemLines = lines.filter((l) => l.state !== "ok");
  const openProblemCount = problemLines.filter((l) => !l.resolved).length;
  const exchangeIncomplete = lines.some((l) => l.state === "exchange" && !l.exchange_to_code);

  let submitLabel = "Kaydet — Eksikler Açık Kalır";
  let submitColor = "bg-amber-600 hover:bg-amber-700";
  if (allPerfect) {
    submitLabel = "Kontrolü Tamamla (Tam Geldi)";
    submitColor = "bg-emerald-600 hover:bg-emerald-700";
  } else if (willClose) {
    submitLabel = "Siparişi Kapat (Kapatıldı)";
    submitColor = "bg-violet-600 hover:bg-violet-700";
  }

  return (
    <div className="max-w-3xl mx-auto fade-up">
      <Link to={backTo} className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4 gap-1">
        <ArrowLeft className="h-4 w-4" /> {backLabel}
      </Link>

      <div className="mb-5">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Sipariş Kontrol</h1>
        <p className="text-sm text-slate-500 mt-1">
          <span className="font-medium text-slate-900">{req.request_no}</span> · {req.supplier_name}
        </p>
        {lines.length > 1 && (
          <div data-testid="receipt-progress" className="mt-2 flex items-center gap-2 text-xs font-medium flex-wrap">
            <span className={`px-2 py-1 rounded-full ${allClosed ? (allPerfect ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-violet-700") : "bg-amber-100 text-amber-700"}`}>
              {closedCount}/{lines.length} kalem kapatıldı
            </span>
            {!allClosed && <span className="text-slate-500">Eksik/hatalı kalemler kontrol için açık kalır.</span>}
          </div>
        )}
      </div>

      {req.receipt && <PreviousCheckSummary receipt={req.receipt} status={req.status} />}

      <div className="space-y-3">
        {lines.map((l) => (
          <ReceiptItemCard key={l.product_code} line={l} onChange={(p) => setLine(l.product_code, p)} />
        ))}
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Genel Not (opsiyonel)</Label>
        <Textarea
          data-testid="overall-note"
          value={overall}
          onChange={(e) => setOverall(e.target.value)}
          placeholder="Bütün sipariş için ek notlarınız..."
          className="mt-1.5 text-base min-h-[70px]"
        />
      </div>

      {exchangeIncomplete && (
        <div data-testid="exchange-incomplete-warning" className="mt-4 bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-800">
          Değişim seçilen kalem(ler) için <span className="font-semibold">değişilecek yeni ürünü</span> seçmeden devam edemezsiniz.
        </div>
      )}

      {problemLines.length > 0 && (
        <div data-testid="discrepancy-bar" className="mt-4 bg-rose-50 border border-rose-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="text-sm text-rose-800">
            <span className="font-semibold">{problemLines.length} sorunlu/iade kalem</span> var.
            {openProblemCount > 0
              ? " Fotoğraflı raporu tedarikçiye/satınalmaya iletin; çözülünce kalemi “Çözüldü” yapıp siparişi kapatın."
              : " Tümü çözüldü — siparişi kapatabilirsiniz."}
          </div>
          <Button
            data-testid="report-discrepancy-open"
            type="button"
            onClick={openReport}
            disabled={busy || exchangeIncomplete}
            className="h-11 gap-2 bg-rose-600 hover:bg-rose-700 text-white shrink-0"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Durumu İlet
          </Button>
        </div>
      )}

      <div className="mt-5 flex flex-col sm:flex-row gap-2 sticky bottom-2 lg:bottom-4 z-20">
        <Link to={`/talepler/${id}`} className="flex-1">
          <Button type="button" variant="outline" className="w-full h-12 bg-white">Vazgeç</Button>
        </Link>
        <Button
          data-testid="submit-receipt"
          type="button"
          disabled={busy || exchangeIncomplete}
          onClick={handleSubmit}
          className={`flex-1 h-12 gap-2 text-white ${submitColor}`}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {submitLabel}
        </Button>
      </div>

      <DiscrepancyReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        request={req}
        problems={problemLines}
        onDone={() => navigate(backTo)}
      />
    </div>
  );
}

function PreviousCheckSummary({ receipt, status }) {
  const problems = (receipt.items || []).filter((p) => p.state !== "ok" && !p.resolved);
  if (problems.length === 0) return null;
  const STATE_BADGE = {
    missing: "bg-amber-100 text-amber-700",
    wrong: "bg-rose-100 text-rose-700",
    damaged: "bg-orange-100 text-orange-700",
    returned: "bg-violet-100 text-violet-700",
    exchange: "bg-teal-100 text-teal-700",
  };
  const STATE_LABEL = { missing: "Eksik", wrong: "Yanlış", damaged: "Hasarlı", returned: "İade Bekleniyor", exchange: "Değişim Bekleniyor" };
  return (
    <div data-testid="prev-check-summary" className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <Info className="h-4 w-4 text-amber-600 shrink-0" />
        <span className="font-semibold text-sm text-amber-900">Önceki kontrolde açık kalan kalemler</span>
        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-200 text-amber-900">{status}</span>
      </div>
      <p className="text-xs text-amber-800 mb-3">
        Bu sipariş daha önce kontrol edildi. Aşağıdaki kalemler hâlâ bekliyor — doğru ürün/iade geldiğinde ilgili kalemi <span className="font-semibold">“Tam”</span> ya da <span className="font-semibold">“Çözüldü”</span> yapın.
      </p>
      <ul className="space-y-2">
        {problems.map((p, i) => (
          <li key={i} data-testid={`prev-problem-${p.product_code}`} className="bg-white border border-amber-200 rounded-md px-2.5 py-2 text-xs">
            <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded-full mb-1.5 ${STATE_BADGE[p.state] || "bg-slate-100 text-slate-700"}`}>
              {STATE_LABEL[p.state] || p.state}
            </span>
            {p.state === "exchange" && p.exchange_to ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-slate-700"><span className="font-semibold">{p.product_code}</span> {p.product_name} <span className="text-slate-400">(yanlış)</span></span>
                <span className="text-teal-600 font-bold">→</span>
                <span className="text-slate-900"><span className="font-semibold">{p.exchange_to.product_code}</span> {p.exchange_to.product_name} <span className="text-teal-600">(doğru/beklenen)</span></span>
              </div>
            ) : (
              <div className="text-slate-700">
                <span className="font-semibold">{p.product_code}</span> {p.product_name}
                {(p.state === "missing" || p.state === "wrong" || p.state === "damaged") && (
                  <span className="text-slate-500"> · Gelen {p.received_qty}/{p.ordered_qty} {p.unit}</span>
                )}
              </div>
            )}
            {p.note && <div className="text-slate-500 mt-1">Not: {p.note}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReceiptItemCard({ line, onChange }) {
  const [editing, setEditing] = useState(!(line.done || line.resolved));

  if (line.done && !editing) {
    return (
      <div data-testid={`receipt-item-${line.product_code}`} className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
            <Check className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 truncate">
              {line.product_code} <span className="text-xs font-normal text-emerald-700">· Tamamlandı</span>
            </div>
            <div className="text-sm text-slate-600 truncate">{line.product_name}</div>
            <div className="text-xs text-slate-500 mt-0.5">Gelen: <span className="font-medium text-slate-900">{line.received_qty} {line.unit}</span></div>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={`edit-${line.product_code}`}
          className="h-9 shrink-0"
          onClick={() => setEditing(true)}
        >
          Düzelt
        </Button>
      </div>
    );
  }

  if (line.resolved && !line.done && !editing) {
    const stLabel = STATES.find((s) => s.key === line.state)?.label || "";
    return (
      <div data-testid={`receipt-item-${line.product_code}`} className="bg-violet-50 border border-violet-200 rounded-lg p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-full bg-violet-600 text-white flex items-center justify-center shrink-0">
            <Check className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 truncate">
              {line.product_code} <span className="text-xs font-normal text-violet-700">· Çözüldü ({stLabel})</span>
            </div>
            <div className="text-sm text-slate-600 truncate">{line.product_name}</div>
            {line.resolution_note && (
              <div className="text-xs text-violet-700 mt-0.5 truncate">Çözüm: {line.resolution_note}</div>
            )}
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={`edit-${line.product_code}`}
          className="h-9 shrink-0"
          onClick={() => setEditing(true)}
        >
          Düzelt
        </Button>
      </div>
    );
  }

  return (
    <div data-testid={`receipt-item-${line.product_code}`} className="bg-white border border-slate-200 rounded-lg p-4">
      {line.replaces_code && (
        <div className="mb-3 text-xs bg-teal-50 border border-teal-200 rounded-md px-2.5 py-2 flex items-start gap-1.5">
          <Repeat className="h-3.5 w-3.5 text-teal-600 mt-0.5 shrink-0" />
          <span className="text-teal-800">
            <span className="font-semibold">Değişim ürünü.</span> Yanlış gelen <span className="font-semibold">{line.replaces_code}</span> {line.replaces_name} yerine bu ürün istendi. Doğru ürün geldiyse <span className="font-semibold">“Tam”</span> seçin.
          </span>
        </div>
      )}
      {!line.replaces_code && ["returned", "missing", "wrong", "damaged", "exchange"].includes(line.prev_state) && (
        <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 text-amber-800">
          Önceki kontrol: <span className="font-semibold">{STATES.find((s) => s.key === line.prev_state)?.label || line.prev_state}</span> — bu kalem açık bırakılmıştı.
        </div>
      )}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900">{line.product_code}</div>
          <div className="text-sm text-slate-600 mt-0.5">{line.product_name}</div>
        </div>
        <div className="text-right text-xs text-slate-500 shrink-0">
          Sipariş: <span className="font-medium text-slate-900">{line.ordered_qty} {line.unit}</span>
        </div>
      </div>

      {/* State chips */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 mb-3">
        {STATES.map((s) => {
          const active = line.state === s.key;
          return (
            <button
              key={s.key}
              type="button"
              data-testid={`state-${line.product_code}-${s.key}`}
              onClick={() => onChange({ state: s.key })}
              className={`text-[10px] font-semibold py-1.5 rounded-md border flex flex-col items-center justify-center gap-0.5 transition-colors
                ${active ? s.activeColor : s.color}`}
            >
              <s.Icon className="h-4 w-4" />
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Gelen Miktar</Label>
          <Input
            data-testid={`received-${line.product_code}`}
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={line.received_qty}
            onChange={(e) => onChange({ received_qty: e.target.value })}
            className="mt-1.5 h-11 text-base text-right"
          />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Birim</Label>
          <div className="mt-1.5 h-11 px-3 flex items-center bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700">{line.unit}</div>
        </div>
      </div>

      {line.state !== "ok" && (
        <>
          <div className="mb-3">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Açıklama</Label>
            <Textarea
              data-testid={`note-${line.product_code}`}
              value={line.note}
              onChange={(e) => onChange({ note: e.target.value })}
              placeholder="Eksik/yanlış/hasar detayı..."
              className="mt-1.5 text-sm min-h-[60px]"
            />
          </div>

          {line.state === "exchange" && (
            <div className="mb-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                <Repeat className="h-3.5 w-3.5 text-teal-600" /> Değişilecek Yeni Ürün <span className="text-rose-600">*</span>
              </Label>
              <p className="text-[11px] text-slate-500 mt-0.5 mb-1.5">Yanlış gelen ürünün yerine gönderilmesini istediğiniz doğru ürünü seçin.</p>
              <ProductPicker
                value={line.exchange_to_code}
                valueName={line.exchange_to_name}
                onSelect={(p) => onChange({ exchange_to_code: p.code, exchange_to_name: p.name, exchange_to_unit: p.unit })}
                onClear={() => onChange({ exchange_to_code: "", exchange_to_name: "", exchange_to_unit: "" })}
                testId={`exchange-product-${line.product_code}`}
              />
              {!line.exchange_to_code && (
                <p data-testid={`exchange-warning-${line.product_code}`} className="text-[11px] text-rose-600 mt-1">
                  Değişim için yeni ürün seçilmeli — talepteki ürün bununla değişecek.
                </p>
              )}
            </div>
          )}

          <PhotoUploader
            paths={line.photo_paths}
            onChange={(paths) => onChange({ photo_paths: paths })}
            testIdPrefix={`photo-${line.product_code}`}
          />

          {/* Resolve / close this problematic item */}
          <div className="mt-3 pt-3 border-t border-slate-100">
            <button
              type="button"
              data-testid={`resolve-${line.product_code}`}
              onClick={() => onChange({ resolved: !line.resolved })}
              className={`w-full h-11 rounded-md border text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors
                ${line.resolved ? "bg-violet-600 text-white border-violet-600 hover:bg-violet-700" : "bg-white text-violet-700 border-violet-200 hover:bg-violet-50"}`}
            >
              <Check className="h-4 w-4" />
              {line.resolved ? "Çözüldü ✓ (geri al)" : "Çözüldü olarak işaretle"}
            </button>
            {line.resolved && (
              <div className="mt-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Çözüm Notu (opsiyonel)</Label>
                <Input
                  data-testid={`resolution-note-${line.product_code}`}
                  value={line.resolution_note || ""}
                  onChange={(e) => onChange({ resolution_note: e.target.value })}
                  placeholder="örn. iade faturası kesildi / 472 adet doğru geldi"
                  className="mt-1.5 h-11 text-sm"
                />
              </div>
            )}
            <p className="text-[11px] text-slate-500 mt-1.5">
              Sorun giderilince (iade işlendi, kredi alındı, doğru ürün geldi) bu kalemi “Çözüldü” yapın. Tüm kalemler çözülünce sipariş kapanır.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function PhotoUploader({ paths, onChange, testIdPrefix }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const newPaths = [...paths];
      for (const f of files) {
        if (f.size > 8 * 1024 * 1024) {
          toast.error(`${f.name}: 8 MB üzeri`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPaths.push(data.path);
      }
      onChange(newPaths);
      toast.success(`${files.length} fotoğraf yüklendi`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Yükleme başarısız");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (idx) => onChange(paths.filter((_, i) => i !== idx));
  const token = localStorage.getItem("access_token") || "";

  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
        <Camera className="h-3.5 w-3.5" /> Fotoğraflar ({paths.length})
      </Label>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        hidden
        data-testid={`${testIdPrefix}-input`}
        onChange={handleUpload}
      />
      <div className="mt-1.5 grid grid-cols-3 sm:grid-cols-4 gap-2">
        {paths.map((p, idx) => (
          <div key={p} data-testid={`${testIdPrefix}-${idx}`} className="relative aspect-square rounded-md overflow-hidden border border-slate-200 bg-slate-50">
            <img
              src={`${API}/files/${p}?auth=${token}`}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <button
              type="button"
              data-testid={`${testIdPrefix}-remove-${idx}`}
              onClick={() => removePhoto(idx)}
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-rose-600 text-white flex items-center justify-center shadow"
              aria-label="Kaldır"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          data-testid={`${testIdPrefix}-add`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="aspect-square rounded-md border-2 border-dashed border-slate-300 hover:border-slate-500 text-slate-400 hover:text-slate-700 flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <Camera className="h-5 w-5" />
              <span className="text-[10px] font-semibold uppercase">Ekle</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
